import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api';
import { formatDimensions, formatWeight } from '../../../shared/units';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';

const MODE_ITEMS = 'items';
const MODE_DEPLOY = 'deploy';
const MODE_RETURN = 'return';
const STATUS_ISSUED = 'in_project';

const contentLabel = (line) => {
  const serial = line.serial_number ? ` (${line.serial_number})` : '';
  return line.kind === 'unit' ? `${line.product_name}${serial}` : `${line.product_name} ×${line.quantity}`;
};

const sumWeights = (cases) => Math.round(cases.reduce((sum, c) => sum + (c.total_weight_display || 0), 0) * 1000) / 1000;

/**
 * Cases of a contract: the case with its box, contents, outer dimensions and weight with contents.
 * mode "items" only lists them, "deploy" issues the boxes of the cases, "return" takes them back.
 */
const ContractCases = ({ contractId, mode = MODE_ITEMS }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState(null);
  const queryKey = ['contract-bundles', contractId];

  const { data: cases = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => api.get(`contracts/api/contract-bundles/?contract=${contractId}`).then((res) => res.results || res || []),
    enabled: !!contractId,
  });

  useRealtimeEvents((e) => {
    if (e.model === 'contracts.contractbundle' || e.model === 'contracts.contractbundlecontent' || e.model === 'warehouses.caseunit') {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const action = mode === MODE_RETURN ? 'return_cases' : 'deploy_cases';
  const { mutate, isPending } = useMutation({
    mutationFn: (ids) => api.post(`contracts/api/contracts/${contractId}/${action}/`, ids ? { contract_bundles: ids } : {}),
    onSuccess: (res) => {
      const errors = (res.data || res).errors || [];
      if (errors.length) {
        setMessage({ type: 'warning', text: errors.map((e) => `${e.case_name}: ${e.error}`).join('; ') });
      } else {
        setMessage({ type: 'success', text: t(mode === MODE_RETURN ? 'contracts.cases.returnSuccess' : 'contracts.cases.deploySuccess') });
      }
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => setMessage({ type: 'danger', text: t('contracts.cases.error') }),
  });

  const isIssued = (c) => c.case_unit_status === STATUS_ISSUED;
  const rows = mode === MODE_RETURN ? cases.filter(isIssued) : cases;
  const actionable = rows.filter((c) => (mode === MODE_RETURN ? isIssued(c) : !isIssued(c)));
  const symbol = cases[0]?.weight_symbol;

  if (isLoading) {
    return <div className="text-center py-3 text-muted"><Spinner animation="border" size="sm" className="me-2" />{t('common.loading')}</div>;
  }
  if (mode === MODE_ITEMS && cases.length === 0) {
    return (
      <div className="text-center py-4 text-muted">
        <small>{t('contracts.cases.noCases')}</small>
      </div>
    );
  }
  if (mode !== MODE_ITEMS && rows.length === 0) {
    return <p className="text-muted">{t(mode === MODE_RETURN ? 'contracts.cases.noIssued' : 'contracts.cases.noCases')}</p>;
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0">{t(mode === MODE_ITEMS ? 'contracts.cases.title' : `contracts.cases.${mode}Title`)}</h5>
        {mode !== MODE_ITEMS && (
          <Button variant="primary" size="sm" onClick={() => mutate(null)} disabled={isPending || actionable.length === 0}>
            {isPending ? <Spinner size="sm" className="me-1" /> : <i className="ti ti-truck me-1"></i>}
            {t(mode === MODE_RETURN ? 'contracts.cases.returnAll' : 'contracts.cases.deployAll')}
          </Button>
        )}
      </div>
      {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}
      <div className="table-responsive">
        <table className="table table-hover table-vcenter">
          <thead>
            <tr>
              <th>{t('contracts.cases.name')}</th>
              <th>{t('contracts.cases.box')}</th>
              <th>{t('contracts.cases.contents')}</th>
              <th>{t('contracts.cases.dimensions')}</th>
              <th className="text-end">{t('contracts.cases.weight')}</th>
              {mode !== MODE_ITEMS && <th>{t('contracts.cases.status')}</th>}
              {mode !== MODE_ITEMS && <th></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td className="fw-medium">{c.case_name}</td>
                <td>
                  {c.case_unit_qr
                    ? <span><code className="small">{c.case_unit_qr}</code>{c.case_unit_stock_name && <span className="text-muted ms-1">({c.case_unit_stock_name})</span>}</span>
                    : <span className="text-muted">{t('contracts.cases.noBox')}</span>}
                </td>
                <td>
                  {c.contents.length === 0
                    ? <span className="text-muted">{t('contracts.cases.emptyCase')}</span>
                    : <ul className="list-unstyled mb-0 small">{c.contents.map((line) => <li key={line.id}>{contentLabel(line)}</li>)}</ul>}
                </td>
                <td className="text-nowrap">
                  {formatDimensions(c.outer_length, c.outer_width, c.outer_height, c.outer_dimension_unit_symbol)}
                </td>
                <td className="text-end text-nowrap">{formatWeight(c.total_weight_display, c.weight_symbol)}</td>
                {mode !== MODE_ITEMS && (
                  <td>
                    <span className={`badge ${isIssued(c) ? 'bg-success' : 'bg-secondary'}`}>
                      {t(isIssued(c) ? 'contracts.cases.issued' : 'contracts.cases.notIssued')}
                    </span>
                  </td>
                )}
                {mode !== MODE_ITEMS && (
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" onClick={() => mutate([c.id])} disabled={isPending || !actionable.includes(c)}>
                      {t(mode === MODE_RETURN ? 'contracts.cases.returnBtn' : 'contracts.cases.deployBtn')}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {mode === MODE_ITEMS && (
            <tfoot>
              <tr>
                <td colSpan={4} className="text-end fw-medium">{t('contracts.cases.totalWeight')}</td>
                <td className="text-end fw-bold text-nowrap">{formatWeight(sumWeights(cases), symbol)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default ContractCases;
